import { cn } from "@/utils/cn";
import { Language, LANGUAGES } from "@/utils/language";
import { useEffect, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiX } from "react-icons/fi";

export interface LanguageInputProps {
  onSelect: (language: Language) => void;
  onClear?: () => void;
  placeholder?: string;
  excludeLanguages?: string[];
  className?: string;
  autoClear?: boolean;
  value?: string;
}

export function LanguageInput({
  onSelect,
  onClear,
  placeholder = "Select a language",
  excludeLanguages = [],
  className = "",
  autoClear = false,
  value,
}: LanguageInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredLanguages = LANGUAGES.filter((lang) => !excludeLanguages.includes(lang.code)).filter(
    (lang) =>
      lang.name.toLowerCase().includes(search.toLowerCase()) || lang.code.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      const language = LANGUAGES.find((lang) => lang.code === value || lang.name === value);
      if (language) {
        setSelectedLanguage(language);
        setSearch(language.name);
      }
    } else {
      setSelectedLanguage(null);
      setSearch("");
    }
  }, [value]);

  const handleClear = () => {
    setSelectedLanguage(null);
    setSearch("");
    inputRef.current?.focus();
    onClear?.();
  };

  const handleSelect = (language: Language) => {
    setSelectedLanguage(language);
    setSearch(language.name);
    setIsOpen(false);
    onSelect(language);

    // If autoClear is enabled, clear the input after selection
    if (autoClear) {
      setTimeout(() => {
        setSelectedLanguage(null);
        setSearch("");
        if (onClear) onClear();
      }, 50); // Small delay to ensure onSelect is processed first
    }
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <div className="flex flex-row border bg-white rounded-md overflow-hidden">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (e.target.value === "") setSelectedLanguage(null);
            setIsOpen(true);
          }}
          onClick={() => setIsOpen(true)}
          placeholder={placeholder}
          className="flex-1 p-2 pl-3 outline-none"
        />

        <button
          disabled={!search}
          type="button"
          onClick={handleClear}
          className={cn("text-gray-400 hover:text-gray-600 transition-colors", { invisible: !search })}
        >
          <FiX />
        </button>

        <div className="w-1" />

        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="px-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <FiChevronDown className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredLanguages.length === 0 ? (
            <div className="px-3 py-2 text-gray-500">No languages found</div>
          ) : (
            filteredLanguages.map((language) => (
              <div
                key={language.code}
                onClick={() => handleSelect(language)}
                className={`flex items-center px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                  selectedLanguage?.code === language.code ? "bg-blue-50" : ""
                }`}
              >
                <span className="text-lg mr-2">{language.flag}</span>
                <span className="flex-1">{language.name}</span>
                {selectedLanguage?.code === language.code && <FiCheck className="text-blue-600" />}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
